import { UNLEASH_FEATURE_FLAGS, POSTHOG_FEATURE_FLAGS } from '@/consts'

// Task 01 / STEP-02 (C-2): the PostHog and Unleash client construction that
// used to live here (both commented out) is removed, along with the
// `setFeatureFlags` mutation and the `initUnleash` / `initPostHogFlags` /
// `setPersonPropertiesForFlags` actions that drove it -- none of that
// machinery was ever invoked (the two init actions were dead code; commit
// was never called), so every flag below has always resolved to its static
// default. Removing the dead machinery changes nothing observable: every
// getter's current effective value is preserved exactly.

export default {
  namespaced: true,
  state: {
    flags: {
      [UNLEASH_FEATURE_FLAGS.CHATBOT]: false,
      [POSTHOG_FEATURE_FLAGS.DASHBOARD_BANNER]: false,
      [POSTHOG_FEATURE_FLAGS.FILTER_ACTIVE_SUBJECTS]: false,
      [POSTHOG_FEATURE_FLAGS.POLL_FLAGS]: false,
      [POSTHOG_FEATURE_FLAGS.QUIZ_STUDY_MATERIALS]: false,
      [POSTHOG_FEATURE_FLAGS.ORBITAL_SEGMENTS]: false,
      [POSTHOG_FEATURE_FLAGS.STUDENT_COACH_REACH_OUT]: false,
      [POSTHOG_FEATURE_FLAGS.PROCRASTINATION_PREVENTION]: false,
      [POSTHOG_FEATURE_FLAGS.MUTED_SUBJECT_ALERTS]: false,
      [POSTHOG_FEATURE_FLAGS.FALL_INCENTIVE_ENROLLMENT]: false,
      [POSTHOG_FEATURE_FLAGS.TUTOR_SESSION_HISTORY]: false,
      [POSTHOG_FEATURE_FLAGS.SESSION_RECAP_DMS]: false,
      [POSTHOG_FEATURE_FLAGS.RECAP_SOCKET_UPDATES]: false,
      [POSTHOG_FEATURE_FLAGS.BF_INTRO_COPY]: false,
      [POSTHOG_FEATURE_FLAGS.SCORECASTER_WOZ]: false,
      [POSTHOG_FEATURE_FLAGS.SCORECASTER_MODAL]: false,
      [POSTHOG_FEATURE_FLAGS.NEW_ELIGIBILITY_FORM_DESIGN]: false,
      // This is an experiment, using multivariant feature flag values
      [POSTHOG_FEATURE_FLAGS.OFFER_GOOGLE_SSO]: false,
      [POSTHOG_FEATURE_FLAGS.TOPIC_CARD_DASHBOARD_REORDER]: 'control',
      [POSTHOG_FEATURE_FLAGS.CC_INTRO_COPY]: 'baseline',
      [POSTHOG_FEATURE_FLAGS.DASHBOARD_REDESIGN]: false,
      [POSTHOG_FEATURE_FLAGS.AUTO_START_COLLEGE_SESSION]: false,
      [POSTHOG_FEATURE_FLAGS.SMS_VERIFICATION]: false,
      [POSTHOG_FEATURE_FLAGS.ELIGIBILITY_EMAIL]: false,
      [POSTHOG_FEATURE_FLAGS.SMS_VERIFICATION_ENABLED_ON_SIGNUP_FLOW]: false,
    },
    flagPayloads: {
      [POSTHOG_FEATURE_FLAGS.ORBITAL_SEGMENTS]: [],
      [POSTHOG_FEATURE_FLAGS.DOWNTIME_BANNER]: '',
      [POSTHOG_FEATURE_FLAGS.TOPIC_CARD_DASHBOARD_REORDER]: null,
      [POSTHOG_FEATURE_FLAGS.SUBJECT_REQUEST_ROLLOUT]: [],
      [POSTHOG_FEATURE_FLAGS.QUIZ_ROLLOUT]: [],
      [POSTHOG_FEATURE_FLAGS.FORCE_DASHBOARD_REDESIGN_ORG]: 'none',
      [POSTHOG_FEATURE_FLAGS.AUTO_START_COLLEGE_SESSION]: '',
      [POSTHOG_FEATURE_FLAGS.BF_INTRO_COPY]: '',
    },
  },
  getters: {
    isDashboardRedesignActive: state =>
      state.flags[UNLEASH_FEATURE_FLAGS.DASHBOARD_REDESIGN],
    isChatbotActive: state => state.flags[UNLEASH_FEATURE_FLAGS.CHATBOT],
    isDashboardBannerActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.DASHBOARD_BANNER],
    isFilterActiveSubjectsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.FILTER_ACTIVE_SUBJECTS],
    isOptionalMiddleSchoolActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.OPTIONAL_MIDDLE_SCHOOL],
    isPollingFlagsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.POLL_FLAGS],
    isQuizStudyMaterialsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.QUIZ_STUDY_MATERIALS],
    offerGoogleSSO: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.OFFER_GOOGLE_SSO],
    orbitalSegments: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.ORBITAL_SEGMENTS],
    isOrbitalSegmentsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.ORBITAL_SEGMENTS],
    downtimeBannerMessage: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.DOWNTIME_BANNER],
    topicCardDashboardReorder: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.TOPIC_CARD_DASHBOARD_REORDER],
    isJustTellThemActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.TOPIC_CARD_DASHBOARD_REORDER] ===
      'tell-them',
    isTopicDashboardReorderActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.TOPIC_CARD_DASHBOARD_REORDER] ===
      'dashboard-reorder',
    subjectRequestRollout: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.SUBJECT_REQUEST_ROLLOUT] ?? [],
    quizRollout: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.QUIZ_ROLLOUT] ?? [],
    ccIntroCopy: state => state.flags[POSTHOG_FEATURE_FLAGS.CC_INTRO_COPY],
    isBfIntroCopyEnabled: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.BF_INTRO_COPY],
    bfIntroCopy: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.BF_INTRO_COPY],
    isProcrastinationPreventionActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.PROCRASTINATION_PREVENTION],
    isMutedSubjectAlertsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.MUTED_SUBJECT_ALERTS],
    isFallIncentiveEnrollmentActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.FALL_INCENTIVE_ENROLLMENT],
    isTutorSessionHistoryActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.TUTOR_SESSION_HISTORY],
    isSessionRecapDmsActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.SESSION_RECAP_DMS],
    showDashboardRedesign: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.DASHBOARD_REDESIGN],
    forceShowDashboardRedesignOrg: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.FORCE_DASHBOARD_REDESIGN_ORG],
    isRecapSocketUpdatesActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.RECAP_SOCKET_UPDATES],
    isAutoStartCollegeSessionActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.AUTO_START_COLLEGE_SESSION],
    autoStartCollegeSession: state =>
      state.flagPayloads[POSTHOG_FEATURE_FLAGS.AUTO_START_COLLEGE_SESSION],
    isScorecasterModalActive: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.SCORECASTER_MODAL],
    isSmsVerificationEnabled: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.SMS_VERIFICATION],
    eligibilityEmail: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.ELIGIBILITY_EMAIL],
    isNewEligibilityFormDesignEnabled: state =>
      state.flags[POSTHOG_FEATURE_FLAGS.NEW_ELIGIBILITY_FORM_DESIGN],
    isSmsVerificationEnabledOnSignupFlow: state =>
      state.flags[
        POSTHOG_FEATURE_FLAGS.SMS_VERIFICATION_ENABLED_ON_SIGNUP_FLOW
      ],
  },
}
